import { useEffect, useState } from 'react';
import { useForecastMap, useMapInteraction } from './ForecastMapContext';
import { windTileTemplate, terrainTileTemplate } from '../../lib/wind/urls';
import { paramForLevel } from '../../lib/wind/levels';
import { barbAtPixel } from '../../lib/wind/windField';
import { msToKt, decodeScalar } from '../../lib/wind/decode';
import { elevationFromRGB, elevAboveLevel } from '../../lib/wind/terrainMask';
import { terrainMaskZoom } from '../../lib/wind/terrainSampler';
import { getDecodedTile } from './wind/tileCache';

const maxZoomFor = (product, param, model) =>
  (model === 'ICON' ? 7 : product.maxTileZoom || (param === 'uv_10' ? 9 : 7));

// Cursor-following readout of the wind speed under the pointer. Decodes the uv
// tile at the cursor (reusing the shared decoded-tile cache the fill/barbs
// already populated), so it's effectively free. Respects the m/s ↔ kt unit.
// Scalar products (convergence/ridge) show the signed value instead — vertical
// motion in m/s, no direction, no kt conversion.
const WindHoverValue = ({ base, date, time, product, level, unit, model }) => {
  const map = useForecastMap();
  const { coarsePointer, claimPeek } = useMapInteraction();
  const [tip, setTip] = useState(null); // { x, y, text }
  const param = product && (product.levelStepped ? paramForLevel(level) : product.param);

  useEffect(() => {
    if (!map || !param || !date || !time) return undefined;
    const range = product.range;
    const template = windTileTemplate({ base, date, time, param, model });
    const maskLevel = product.levelStepped ? level : null;
    const terrainTemplate = terrainTileTemplate(base);
    let seq = 0;
    let dismissTimer = null;

    // Returns { x, y, text } to show, null to hide, or undefined when stale / no
    // tile (leave the current tip unchanged). Shared by hover + tap.
    const lookup = async (e, my) => {
      const z = Math.max(3, Math.min(maxZoomFor(product, param, model), Math.round(map.getZoom())));
      const n = Math.pow(2, z);
      const fx = ((e.lngLat.lng + 180) / 360) * n;
      const fy = ((1 - Math.asinh(Math.tan((e.lngLat.lat * Math.PI) / 180)) / Math.PI) / 2) * n;
      const tx = Math.floor(fx), ty = Math.floor(fy);
      if (maskLevel != null) {
        const tz = terrainMaskZoom(map.getZoom());
        const tn = Math.pow(2, tz);
        const tfx = ((e.lngLat.lng + 180) / 360) * tn;
        const tfy = ((1 - Math.asinh(Math.tan((e.lngLat.lat * Math.PI) / 180)) / Math.PI) / 2) * tn;
        const ttx = Math.floor(tfx), tty = Math.floor(tfy);
        const turl = terrainTemplate.replace('{z}', tz).replace('{x}', ttx).replace('{y}', tty);
        const tt = await getDecodedTile(turl).catch(() => null);
        if (my !== seq) return undefined;
        if (tt) {
          const tpx = Math.min(tt.w - 1, Math.floor((tfx - ttx) * tt.w));
          const tpy = Math.min(tt.h - 1, Math.floor((tfy - tty) * tt.h));
          const ti = (tpy * tt.w + tpx) * 4;
          if (elevAboveLevel(elevationFromRGB(tt.data[ti], tt.data[ti + 1], tt.data[ti + 2]), maskLevel)) return null;
        }
      }
      const url = template.replace('{z}', z).replace('{x}', tx).replace('{y}', ty);
      const tile = await getDecodedTile(url).catch(() => null);
      if (!tile || my !== seq) return undefined;
      const px = Math.min(tile.w - 1, Math.floor((fx - tx) * tile.w));
      const py = Math.min(tile.h - 1, Math.floor((fy - ty) * tile.h));
      if (product.kind === 'scalar') {
        const v = decodeScalar(tile.data[(py * tile.w + px) * 4] / 255, range.min, range.max);
        if (v === null) return null;
        return { x: e.point.x, y: e.point.y, text: `${v > 0 ? '+' : ''}${v.toFixed(1)} m/s` };
      }
      const barb = barbAtPixel(tile.data, tile.w, tile.h, px, py, range);
      if (!barb) return null;
      const text = unit === 'kt'
        ? `${Math.round(msToKt(barb.speedMs))} kt`
        : `${barb.speedMs.toFixed(1)} m/s`;
      return { x: e.point.x, y: e.point.y, text: `${text} · ${barb.wdir}°` };
    };

    const onMove = async (e) => {
      const my = ++seq;
      clearTimeout(dismissTimer);
      const r = await lookup(e, my);
      if (r === undefined || my !== seq) return;
      setTip(r);
    };
    const onMoveStart = () => setTip(null);
    const onTap = async (e) => {
      const my = ++seq;
      const r = await lookup(e, my);
      if (r === undefined || my !== seq) return;
      setTip(r);
      if (r) {
        claimPeek();
        clearTimeout(dismissTimer);
        dismissTimer = setTimeout(() => setTip(null), 4000);
      }
    };
    const onLeave = () => setTip(null);

    map.on('mousemove', onMove);
    map.on('mouseout', onLeave);
    if (coarsePointer) { map.on('click', onTap); map.on('movestart', onMoveStart); }
    return () => {
      clearTimeout(dismissTimer);
      map.off('mousemove', onMove);
      map.off('mouseout', onLeave);
      if (coarsePointer) { map.off('click', onTap); map.off('movestart', onMoveStart); }
      setTip(null);
    };
  }, [map, base, date, time, param, product, level, unit, model, coarsePointer, claimPeek]);

  if (!tip) return null;
  return (
    <div
      className="pointer-events-none absolute z-[6] px-1.5 py-0.5 rounded bg-slate-900/85 text-white text-[11px] font-medium tabular-nums shadow"
      style={{ left: tip.x, top: tip.y, transform: 'translate(12px, -50%)', textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)' }}
    >
      {tip.text}
    </div>
  );
};

export default WindHoverValue;
